import { TOOL_NAME_SEPARATOR } from "@app/lib/actions/constants";
import { getDataSourceFileSystemCatReadTarget } from "@app/lib/actions/data_source_file_system_read_targets";
import type { InternalMCPServerNameType } from "@app/lib/actions/mcp_internal_actions/constants";
import {
  AVAILABLE_INTERNAL_MCP_SERVER_NAMES,
  getInternalMCPServerToolDisplayLabels,
} from "@app/lib/actions/mcp_internal_actions/constants";
import { DEFAULT_REMOTE_MCP_SERVERS } from "@app/lib/actions/mcp_internal_actions/remote_servers";
import {
  isDataSourceFilesystemFindInputType,
  isGenerateImageInputType,
  isSearchInputTypeWithTags,
  isWebbrowseInputType,
  isWebsearchInputType,
} from "@app/lib/actions/mcp_internal_actions/types";
import type { ToolDisplayLabels } from "@app/lib/api/mcp";
import { t, tp } from "@app/lib/compta/i18n";
import { nomCompetenceDepuisNom } from "@app/lib/compta/libelles_competences";
import {
  libellesAffichageOutilGoogle,
  libellesAffichageOutilMetier,
} from "@app/lib/compta/libelles_outils";
import { formatNumber } from "@app/lib/i18n/format";
import { stripFileExtension } from "@app/types/files";
import {
  parseCanonicalScopedPath,
  TOOL_OUTPUTS_FOLDER_NAME,
} from "@app/types/mount_path";
import { assertNever } from "@app/types/shared/utils/assert_never";
import {
  isNumber,
  isString,
  isStringArray,
} from "@app/types/shared/utils/general";
import { asDisplayName, slugify } from "@app/types/shared/utils/string_utils";

type ToolDisplayLabelsByTool = Record<string, ToolDisplayLabels>;

const MAX_QUERY_DISPLAY_LENGTH = 60;

function truncateQuery(query: string): string {
  return query.length > MAX_QUERY_DISPLAY_LENGTH
    ? query.slice(0, MAX_QUERY_DISPLAY_LENGTH) + "…"
    : query;
}

function shortenUrl(url: string): string {
  return truncateQuery(url.replace(/^https?:\/\//, ""));
}

function formatStringList(values: string[]): string {
  return truncateQuery(values.join(", "));
}

function formatEmailRecipientSuffix(to: unknown): string {
  if (!isStringArray(to)) {
    return "";
  }

  if (to.length === 1) {
    return tp(" to {to}", { to: truncateQuery(to[0]) });
  }

  return to.length > 1 ? t(" to several recipients") : "";
}

function getFilePathReadTarget(filePath: string): string {
  const scopedPath = parseCanonicalScopedPath(filePath);
  if (!scopedPath || !scopedPath.relPath) {
    return tp("“{path}”", { path: truncateQuery(filePath) });
  }

  let displayName = scopedPath.relPath;
  if (scopedPath.relPath.startsWith(`${TOOL_OUTPUTS_FOLDER_NAME}/`)) {
    const fileName = scopedPath.relPath.split("/").at(-1) ?? scopedPath.relPath;

    // Tool output filenames use a 13-digit timestamp prefix for ordering and uniqueness.
    // Hide this storage detail from the user-facing label.
    displayName = fileName.replace(/^\d{13}_/, "");
  }

  switch (scopedPath.scope.kind) {
    case "canonical-conversation":
      return tp("“{name}” from conversation", {
        name: truncateQuery(displayName),
      });
    case "canonical-pod":
      return tp("“{name}” from Pod", { name: truncateQuery(displayName) });
    default:
      return assertNever(scopedPath.scope);
  }
}

// Fork : cible de lecture d'une source de données en français (« GitHub issue #12 » devient
// « ticket GitHub #12 ») ; le numéro final reste hors de la clé du dictionnaire.
function traduireCibleLecture(cible: string): string {
  const numero = cible.match(/^(.*)( #\d+)$/);
  return numero ? `${t(numero[1])}${numero[2]}` : t(cible);
}

// Fork : libellés statiques des serveurs amont passés par le dictionnaire (une clé absente
// reste en anglais).
function traduireLibellesStatiques(
  libelles: ToolDisplayLabels | undefined
): ToolDisplayLabels | undefined {
  if (!libelles) {
    return undefined;
  }
  return {
    ...libelles,
    running: t(libelles.running),
    done: t(libelles.done),
  };
}

const INTERNAL_TOOL_DISPLAY_LABELS_BY_SERVER = Object.fromEntries(
  AVAILABLE_INTERNAL_MCP_SERVER_NAMES.flatMap((serverName) => {
    const displayLabels = getInternalMCPServerToolDisplayLabels(serverName);

    if (!displayLabels) {
      return [];
    }

    return [[slugify(serverName), displayLabels] as const];
  })
) as Record<string, ToolDisplayLabelsByTool>;

const DEFAULT_REMOTE_TOOL_DISPLAY_LABELS_BY_SERVER = Object.fromEntries(
  DEFAULT_REMOTE_MCP_SERVERS.flatMap((server) => {
    if (!server.toolDisplayLabels) {
      return [];
    }

    return [[slugify(server.name), server.toolDisplayLabels] as const];
  })
) as Record<string, ToolDisplayLabelsByTool>;

function getToolCallNameParts(functionCallName: string) {
  const separatorIndex = functionCallName.lastIndexOf(TOOL_NAME_SEPARATOR);

  if (separatorIndex === -1) {
    return null;
  }

  return {
    serverName: functionCallName.slice(0, separatorIndex),
    toolName: functionCallName.slice(
      separatorIndex + TOOL_NAME_SEPARATOR.length
    ),
  };
}

/**
 * Generate input-aware dynamic labels for internal MCP server tools.
 */
function getDynamicToolDisplayLabels({
  internalMCPServerName,
  toolName,
  inputs,
}: {
  internalMCPServerName: InternalMCPServerNameType;
  toolName: string;
  inputs: Record<string, unknown>;
}): ToolDisplayLabels | null {
  switch (internalMCPServerName) {
    case "web_search_&_browse":
    case "http_client":
      if (toolName === "websearch" && isWebsearchInputType(inputs)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching “{q}”", { q }),
          done: tp("Search “{q}”", { q }),
        };
      }
      if (toolName === "webbrowser" && isWebbrowseInputType(inputs)) {
        if (inputs.urls.length === 1) {
          const url = shortenUrl(inputs.urls[0]);
          return {
            running: tp("Browsing “{url}”", { url }),
            done: tp("Browse “{url}”", { url }),
          };
        }
        const count = inputs.urls.length;
        return {
          running: tp("Browsing {count} web pages", { count }),
          done: tp("Browse {count} web pages", { count }),
        };
      }
      return null;

    case "pod_manager":
      if (toolName === "semantic_search" && isSearchInputTypeWithTags(inputs)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching “{q}” in Pod", { q }),
          done: tp("Search “{q}” in Pod", { q }),
        };
      }
      return null;

    case "search":
      if (toolName === "semantic_search" && isSearchInputTypeWithTags(inputs)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching “{q}”", { q }),
          done: tp("Search “{q}”", { q }),
        };
      }
      return null;

    case "data_sources_file_system":
      if (
        toolName === "find" &&
        isDataSourceFilesystemFindInputType(inputs) &&
        inputs.query
      ) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Finding “{q}”", { q }),
          done: tp("Find “{q}”", { q }),
        };
      }
      if (toolName === "semantic_search" && isSearchInputTypeWithTags(inputs)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching “{q}”", { q }),
          done: tp("Search “{q}”", { q }),
        };
      }
      if (toolName === "cat") {
        const target = traduireCibleLecture(
          getDataSourceFileSystemCatReadTarget(inputs)
        );

        if (isString(inputs.grep)) {
          const g = truncateQuery(inputs.grep);
          return {
            running: tp("Searching for “{g}” in {target}", { g, target }),
            done: tp("Search for “{g}” in {target}", { g, target }),
          };
        }
        const offset = isNumber(inputs.offset)
          ? formatNumber(inputs.offset)
          : null;
        const limit = isNumber(inputs.limit)
          ? formatNumber(inputs.limit)
          : null;

        if (offset && limit) {
          return {
            running: tp("Reading {target} (next ~{limit} characters)", {
              target,
              limit,
            }),
            done: tp("Read {target} (next ~{limit} characters)", {
              target,
              limit,
            }),
          };
        } else if (limit) {
          return {
            running: tp("Reading {target} (first ~{limit} characters)", {
              target,
              limit,
            }),
            done: tp("Read {target} (first ~{limit} characters)", {
              target,
              limit,
            }),
          };
        } else if (offset) {
          return {
            running: tp("Reading {target} (from character {offset})", {
              target,
              offset,
            }),
            done: tp("Read {target} (from character {offset})", {
              target,
              offset,
            }),
          };
        }
        return {
          running: tp("Reading {target}", { target }),
          done: tp("Read {target}", { target }),
        };
      }
      return null;

    case "image_generation":
      if (toolName === "generate_image" && isGenerateImageInputType(inputs)) {
        const name = truncateQuery(stripFileExtension(inputs.outputName));
        return {
          running: tp("Generating image “{name}”", { name }),
          done: tp("Generate image “{name}”", { name }),
        };
      }
      return null;

    case "gmail":
      if (
        (toolName === "get_drafts" || toolName === "get_messages") &&
        isString(inputs.q)
      ) {
        const q = truncateQuery(inputs.q);
        return {
          running: tp("Searching Gmail “{q}”", { q }),
          done: tp("Search Gmail “{q}”", { q }),
        };
      }
      if (toolName === "create_draft" && isString(inputs.subject)) {
        const s = truncateQuery(inputs.subject);
        const recipient = formatEmailRecipientSuffix(inputs.to);
        return {
          running: tp("Drafting email “{s}”{recipient}", { s, recipient }),
          done: tp("Draft email “{s}”{recipient}", { s, recipient }),
        };
      }
      if (toolName === "send_mail" && isString(inputs.subject)) {
        const s = truncateQuery(inputs.subject);
        const recipient = formatEmailRecipientSuffix(inputs.to);
        return {
          running: tp("Sending email “{s}”{recipient}", { s, recipient }),
          done: tp("Send email “{s}”{recipient}", { s, recipient }),
        };
      }
      if (toolName === "delete_draft" && isString(inputs.subject)) {
        const s = truncateQuery(inputs.subject);
        return {
          running: tp("Deleting draft “{s}”", { s }),
          done: tp("Delete draft “{s}”", { s }),
        };
      }
      if (toolName === "get_attachment" && isString(inputs.filename)) {
        const name = truncateQuery(inputs.filename);
        return {
          running: tp("Getting attachment “{name}”", { name }),
          done: tp("Get attachment “{name}”", { name }),
        };
      }
      return null;

    case "slack":
    case "slack_bot":
      if (toolName === "post_message") {
        if (isString(inputs.to)) {
          const to = truncateQuery(inputs.to);
          return {
            running: tp("Posting Slack message to {to}", { to }),
            done: tp("Post Slack message to {to}", { to }),
          };
        }
        if (isStringArray(inputs.to)) {
          const to = tp("{count} recipients", { count: inputs.to.length });
          return {
            running: tp("Posting Slack message to {to}", { to }),
            done: tp("Post Slack message to {to}", { to }),
          };
        }
      }
      if (
        toolName === "search_messages" &&
        isStringArray(inputs.keywords) &&
        inputs.keywords.length > 0
      ) {
        const keywords = formatStringList(inputs.keywords);
        return {
          running: tp("Searching Slack “{keywords}”", { keywords }),
          done: tp("Search Slack “{keywords}”", { keywords }),
        };
      }
      if (toolName === "semantic_search_messages" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Slack “{q}”", { q }),
          done: tp("Search Slack “{q}”", { q }),
        };
      }
      if (toolName === "schedule_message" && isString(inputs.to)) {
        const to = truncateQuery(inputs.to);
        return {
          running: tp("Scheduling message to {to}", { to }),
          done: tp("Schedule message to {to}", { to }),
        };
      }
      if (toolName === "search_user" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Slack users “{q}”", { q }),
          done: tp("Search Slack users “{q}”", { q }),
        };
      }
      if (toolName === "search_channels" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Slack channels “{q}”", { q }),
          done: tp("Search Slack channels “{q}”", { q }),
        };
      }
      if (toolName === "list_messages" && isString(inputs.channel)) {
        const channel = truncateQuery(inputs.channel);
        return {
          running: tp("Listing Slack messages in {channel}", { channel }),
          done: tp("List Slack messages in {channel}", { channel }),
        };
      }
      if (toolName === "read_thread_messages" && isString(inputs.channel)) {
        const channel = truncateQuery(inputs.channel);
        return {
          running: tp("Reading Slack thread in {channel}", { channel }),
          done: tp("Read Slack thread in {channel}", { channel }),
        };
      }
      if (toolName === "create_channel" && isString(inputs.name)) {
        const name = truncateQuery(inputs.name);
        return {
          running: tp("Creating Slack channel {name}", { name }),
          done: tp("Create Slack channel {name}", { name }),
        };
      }
      if (toolName === "invite_to_channel" && isString(inputs.channel)) {
        const channel = truncateQuery(inputs.channel);
        return {
          running: tp("Inviting users to Slack channel {channel}", { channel }),
          done: tp("Invite users to Slack channel {channel}", { channel }),
        };
      }
      if (toolName === "archive_channel" && isString(inputs.channel)) {
        const channel = truncateQuery(inputs.channel);
        return {
          running: tp("Archiving Slack channel {channel}", { channel }),
          done: tp("Archive Slack channel {channel}", { channel }),
        };
      }
      return null;

    case "notion":
      if (toolName === "search" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Notion “{q}”", { q }),
          done: tp("Search Notion “{q}”", { q }),
        };
      }
      return null;

    case "extract_data":
      if (
        toolName === "extract_information_from_documents" &&
        isString(inputs.objective)
      ) {
        const o = truncateQuery(inputs.objective);
        return {
          running: tp("Extracting “{o}”", { o }),
          done: tp("Extract “{o}”", { o }),
        };
      }
      return null;

    case "conversation_files":
      if (toolName === "semantic_search" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching files “{q}”", { q }),
          done: tp("Search files “{q}”", { q }),
        };
      }
      if (toolName === "cat") {
        if (isString(inputs.grep)) {
          const g = truncateQuery(inputs.grep);
          return {
            running: tp("Searching for “{g}” in file", { g }),
            done: tp("Search for “{g}” in file", { g }),
          };
        }
        const offset = isNumber(inputs.offset)
          ? formatNumber(inputs.offset)
          : null;
        const limit = isNumber(inputs.limit)
          ? formatNumber(inputs.limit)
          : null;

        if (offset && limit) {
          return {
            running: tp("Reading file (next ~{limit} characters)", { limit }),
            done: tp("Read file (next ~{limit} characters)", { limit }),
          };
        } else if (limit) {
          return {
            running: tp("Reading file (first ~{limit} characters)", { limit }),
            done: tp("Read file (first ~{limit} characters)", { limit }),
          };
        } else if (offset) {
          return {
            running: tp("Reading file (from character {offset})", { offset }),
            done: tp("Read file (from character {offset})", { offset }),
          };
        }
        return {
          running: t("Reading file"),
          done: t("Read file"),
        };
      }
      return null;

    case "files":
      if (toolName === "cat" && isString(inputs.path)) {
        const target = getFilePathReadTarget(inputs.path);
        return {
          running: tp("Reading {target}", { target }),
          done: tp("Read {target}", { target }),
        };
      }
      if (
        toolName === "grep" &&
        isString(inputs.path) &&
        isString(inputs.pattern)
      ) {
        const path = truncateQuery(inputs.path);
        const pattern = truncateQuery(inputs.pattern);
        return {
          running: tp("Searching “{pattern}” in “{path}”", { pattern, path }),
          done: tp("Search “{pattern}” in “{path}”", { pattern, path }),
        };
      }
      if (toolName === "create" && isString(inputs.path)) {
        const path = truncateQuery(inputs.path);
        return {
          running: tp("Writing “{path}”", { path }),
          done: tp("Write “{path}”", { path }),
        };
      }
      if (toolName === "delete" && isString(inputs.path)) {
        const path = truncateQuery(inputs.path);
        return {
          running: tp("Deleting “{path}”", { path }),
          done: tp("Delete “{path}”", { path }),
        };
      }
      if (
        (toolName === "copy" || toolName === "move") &&
        isString(inputs.source) &&
        isString(inputs.dest)
      ) {
        const source = truncateQuery(inputs.source);
        const dest = truncateQuery(inputs.dest);
        const params = { source, dest };
        return toolName === "copy"
          ? {
              running: tp("Copying “{source}” to “{dest}”", params),
              done: tp("Copy “{source}” to “{dest}”", params),
            }
          : {
              running: tp("Moving “{source}” to “{dest}”", params),
              done: tp("Move “{source}” to “{dest}”", params),
            };
      }
      if (toolName === "resolve" && isString(inputs.file_id)) {
        const fileId = truncateQuery(inputs.file_id);
        return {
          running: tp("Resolving file “{fileId}”", { fileId }),
          done: tp("Resolve file “{fileId}”", { fileId }),
        };
      }
      return null;

    case "github": {
      const base =
        isString(inputs.owner) && isString(inputs.repo)
          ? `github.com/${inputs.owner}/${inputs.repo}`
          : null;
      if (
        toolName === "get_pull_request" &&
        base &&
        isNumber(inputs.pullNumber)
      ) {
        const url = `${base}/pull/${inputs.pullNumber}`;
        return {
          running: tp("Retrieving {url}", { url }),
          done: tp("Retrieve {url}", { url }),
        };
      }
      if (toolName === "get_issue" && base && isNumber(inputs.issueNumber)) {
        const url = `${base}/issues/${inputs.issueNumber}`;
        return {
          running: tp("Retrieving {url}", { url }),
          done: tp("Retrieve {url}", { url }),
        };
      }
      if (toolName === "create_issue" && base && isString(inputs.title)) {
        const title = truncateQuery(inputs.title);
        return {
          running: tp("Creating issue on {base}: “{title}”", { base, title }),
          done: tp("Create issue on {base}: “{title}”", { base, title }),
        };
      }
      if (
        toolName === "comment_on_issue" &&
        base &&
        isNumber(inputs.issueNumber)
      ) {
        const url = `${base}/issues/${inputs.issueNumber}`;
        return {
          running: tp("Commenting on {url}", { url }),
          done: tp("Comment on {url}", { url }),
        };
      }
      if (
        toolName === "create_pull_request_review" &&
        base &&
        isNumber(inputs.pullNumber)
      ) {
        const url = `${base}/pull/${inputs.pullNumber}`;
        return {
          running: tp("Reviewing {url}", { url }),
          done: tp("Review {url}", { url }),
        };
      }
      if (toolName === "list_issues" && base) {
        return {
          running: tp("Listing issues on {base}", { base }),
          done: tp("List issues on {base}", { base }),
        };
      }
      if (toolName === "list_pull_requests" && base) {
        return {
          running: tp("Listing PRs on {base}", { base }),
          done: tp("List PRs on {base}", { base }),
        };
      }
      return null;
    }

    case "google_calendar":
      if (toolName === "create_event" && isString(inputs.summary)) {
        const s = truncateQuery(inputs.summary);
        return {
          running: tp("Creating event “{s}”", { s }),
          done: tp("Create event “{s}”", { s }),
        };
      }
      if (toolName === "update_event" && isString(inputs.summary)) {
        const s = truncateQuery(inputs.summary);
        return {
          running: tp("Updating event “{s}”", { s }),
          done: tp("Update event “{s}”", { s }),
        };
      }
      return null;

    case "outlook_calendar":
      if (
        (toolName === "create_event" || toolName === "update_event") &&
        isString(inputs.subject)
      ) {
        const s = truncateQuery(inputs.subject);
        return toolName === "create_event"
          ? {
              running: tp("Creating event “{s}”", { s }),
              done: tp("Create event “{s}”", { s }),
            }
          : {
              running: tp("Updating event “{s}”", { s }),
              done: tp("Update event “{s}”", { s }),
            };
      }
      return null;

    case "outlook":
      if (toolName === "create_draft" && isString(inputs.subject)) {
        const s = truncateQuery(inputs.subject);
        const recipient = formatEmailRecipientSuffix(inputs.to);
        return {
          running: tp("Drafting email “{s}”{recipient}", { s, recipient }),
          done: tp("Draft email “{s}”{recipient}", { s, recipient }),
        };
      }
      if (toolName === "send_mail" && isString(inputs.subject)) {
        const s = truncateQuery(inputs.subject);
        const recipient = formatEmailRecipientSuffix(inputs.to);
        return {
          running: tp("Sending email “{s}”{recipient}", { s, recipient }),
          done: tp("Send email “{s}”{recipient}", { s, recipient }),
        };
      }
      return null;

    case "google_drive":
      if (toolName === "search_files" && isString(inputs.q)) {
        const q = truncateQuery(inputs.q);
        return {
          running: tp("Searching Drive “{q}”", { q }),
          done: tp("Search Drive “{q}”", { q }),
        };
      }
      if (
        (toolName === "create_document" ||
          toolName === "create_spreadsheet" ||
          toolName === "create_presentation") &&
        isString(inputs.title)
      ) {
        const title = truncateQuery(inputs.title);
        return {
          running: tp("Creating “{title}”", { title }),
          done: tp("Create “{title}”", { title }),
        };
      }
      if (toolName === "create_folder" && isString(inputs.name)) {
        const name = truncateQuery(inputs.name);
        return {
          running: tp("Creating folder “{name}”", { name }),
          done: tp("Create folder “{name}”", { name }),
        };
      }
      if (toolName === "get_worksheet" && isString(inputs.range)) {
        const range = truncateQuery(inputs.range);
        return {
          running: tp("Retrieving worksheet range “{range}”", { range }),
          done: tp("Retrieve worksheet range “{range}”", { range }),
        };
      }
      if (toolName === "copy_file" && isString(inputs.name)) {
        const name = truncateQuery(inputs.name);
        return {
          running: tp("Copying Drive file to “{name}”", { name }),
          done: tp("Copy Drive file to “{name}”", { name }),
        };
      }
      if (toolName === "share_file") {
        const recipient = isString(inputs.emailAddress)
          ? truncateQuery(inputs.emailAddress)
          : isString(inputs.domain)
            ? truncateQuery(inputs.domain)
            : null;
        if (recipient) {
          return {
            running: tp("Sharing Drive file with {recipient}", { recipient }),
            done: tp("Share Drive file with {recipient}", { recipient }),
          };
        }
      }
      if (toolName === "create_comment" && isString(inputs.content)) {
        const content = truncateQuery(inputs.content);
        return {
          running: tp("Commenting on Drive file: {content}", { content }),
          done: tp("Comment on Drive file: {content}", { content }),
        };
      }
      return null;

    case "google_sheets":
      if (toolName === "list_spreadsheets" && isString(inputs.nameFilter)) {
        const q = truncateQuery(inputs.nameFilter);
        return {
          running: tp("Listing Sheets matching “{q}”", { q }),
          done: tp("List Sheets matching “{q}”", { q }),
        };
      }
      if (
        (toolName === "get_worksheet" ||
          toolName === "update_cells" ||
          toolName === "append_data" ||
          toolName === "clear_range") &&
        isString(inputs.range)
      ) {
        const range = truncateQuery(inputs.range);
        const [running, done] =
          toolName === "get_worksheet"
            ? ["Reading Sheets range “{range}”", "Read Sheets range “{range}”"]
            : toolName === "update_cells"
              ? [
                  "Updating Sheets range “{range}”",
                  "Update Sheets range “{range}”",
                ]
              : toolName === "append_data"
                ? [
                    "Appending to Sheets range “{range}”",
                    "Append to Sheets range “{range}”",
                  ]
                : [
                    "Clearing Sheets range “{range}”",
                    "Clear Sheets range “{range}”",
                  ];
        return {
          running: tp(running, { range }),
          done: tp(done, { range }),
        };
      }
      if (toolName === "create_spreadsheet" && isString(inputs.title)) {
        const title = truncateQuery(inputs.title);
        return {
          running: tp("Creating spreadsheet “{title}”", { title }),
          done: tp("Create spreadsheet “{title}”", { title }),
        };
      }
      if (toolName === "add_worksheet" && isString(inputs.title)) {
        const title = truncateQuery(inputs.title);
        return {
          running: tp("Adding worksheet “{title}”", { title }),
          done: tp("Add worksheet “{title}”", { title }),
        };
      }
      if (toolName === "rename_worksheet" && isString(inputs.newTitle)) {
        const title = truncateQuery(inputs.newTitle);
        return {
          running: tp("Renaming worksheet to “{title}”", { title }),
          done: tp("Rename worksheet to “{title}”", { title }),
        };
      }
      return null;

    case "microsoft_drive":
      if (
        (toolName === "search_in_files" || toolName === "search_drive_items") &&
        isString(inputs.query)
      ) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching OneDrive/SharePoint “{q}”", { q }),
          done: tp("Search OneDrive/SharePoint “{q}”", { q }),
        };
      }
      if (toolName === "upload_file") {
        const name = isString(inputs.fileName)
          ? truncateQuery(inputs.fileName)
          : isString(inputs.fileId)
            ? truncateQuery(inputs.fileId)
            : null;
        if (name) {
          return {
            running: tp("Uploading “{name}” to OneDrive/SharePoint", { name }),
            done: tp("Upload “{name}” to OneDrive/SharePoint", { name }),
          };
        }
      }
      if (toolName === "copy_file" && isString(inputs.name)) {
        const name = truncateQuery(inputs.name);
        return {
          running: tp("Copying OneDrive/SharePoint file to “{name}”", { name }),
          done: tp("Copy OneDrive/SharePoint file to “{name}”", { name }),
        };
      }
      return null;

    case "microsoft_excel":
      if (toolName === "list_excel_files" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Listing Excel files matching “{q}”", { q }),
          done: tp("List Excel files matching “{q}”", { q }),
        };
      }
      if (
        (toolName === "read_worksheet" ||
          toolName === "write_worksheet" ||
          toolName === "clear_range") &&
        isString(inputs.worksheetName)
      ) {
        const worksheetName = truncateQuery(inputs.worksheetName);
        const range = isString(inputs.range)
          ? tp(" range “{range}”", { range: truncateQuery(inputs.range) })
          : "";
        const [running, done] =
          toolName === "read_worksheet"
            ? [
                "Reading Excel worksheet “{worksheetName}”{range}",
                "Read Excel worksheet “{worksheetName}”{range}",
              ]
            : toolName === "write_worksheet"
              ? [
                  "Writing Excel worksheet “{worksheetName}”{range}",
                  "Write Excel worksheet “{worksheetName}”{range}",
                ]
              : [
                  "Clearing Excel worksheet “{worksheetName}”{range}",
                  "Clear Excel worksheet “{worksheetName}”{range}",
                ];
        return {
          running: tp(running, { worksheetName, range }),
          done: tp(done, { worksheetName, range }),
        };
      }
      if (toolName === "create_worksheet" && isString(inputs.worksheetName)) {
        const worksheetName = truncateQuery(inputs.worksheetName);
        return {
          running: tp("Creating Excel worksheet “{worksheetName}”", {
            worksheetName,
          }),
          done: tp("Create Excel worksheet “{worksheetName}”", {
            worksheetName,
          }),
        };
      }
      return null;

    case "microsoft_teams":
      if (toolName === "search_messages_content" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Teams messages “{q}”", { q }),
          done: tp("Search Teams messages “{q}”", { q }),
        };
      }
      if (isString(inputs.nameFilter)) {
        const q = truncateQuery(inputs.nameFilter);
        switch (toolName) {
          case "list_users":
            return {
              running: tp("Listing Teams users matching “{q}”", { q }),
              done: tp("List Teams users matching “{q}”", { q }),
            };
          case "list_channels":
            return {
              running: tp("Listing Teams channels matching “{q}”", { q }),
              done: tp("List Teams channels matching “{q}”", { q }),
            };
          case "list_chats":
            return {
              running: tp("Listing Teams chats matching “{q}”", { q }),
              done: tp("List Teams chats matching “{q}”", { q }),
            };
        }
      }
      if (toolName === "post_message" && isString(inputs.targetType)) {
        const targetType = truncateQuery(inputs.targetType);
        return {
          running: tp("Posting Teams message to {targetType}", { targetType }),
          done: tp("Post Teams message to {targetType}", { targetType }),
        };
      }
      if (toolName === "list_meetings" && isString(inputs.subjectFilter)) {
        const subject = truncateQuery(inputs.subjectFilter);
        return {
          running: tp("Listing Teams meetings “{subject}”", { subject }),
          done: tp("List Teams meetings “{subject}”", { subject }),
        };
      }
      if (toolName === "get_transcript_content" && isString(inputs.joinUrl)) {
        const url = shortenUrl(inputs.joinUrl);
        return {
          running: tp("Getting Teams transcript for “{url}”", { url }),
          done: tp("Get Teams transcript for “{url}”", { url }),
        };
      }
      return null;

    case "salesforce":
      if (toolName === "execute_read_query" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Executing Salesforce query “{q}”", { q }),
          done: tp("Execute Salesforce query “{q}”", { q }),
        };
      }
      if (toolName === "describe_object" && isString(inputs.objectName)) {
        const objectName = truncateQuery(inputs.objectName);
        return {
          running: tp("Describing Salesforce object {objectName}", {
            objectName,
          }),
          done: tp("Describe Salesforce object {objectName}", { objectName }),
        };
      }
      if (
        (toolName === "create_object" || toolName === "update_object") &&
        isString(inputs.objectName)
      ) {
        const objectName = truncateQuery(inputs.objectName);
        return toolName === "create_object"
          ? {
              running: tp("Creating Salesforce {objectName} records", {
                objectName,
              }),
              done: tp("Create Salesforce {objectName} records", {
                objectName,
              }),
            }
          : {
              running: tp("Updating Salesforce {objectName} records", {
                objectName,
              }),
              done: tp("Update Salesforce {objectName} records", {
                objectName,
              }),
            };
      }
      if (
        (toolName === "list_attachments" || toolName === "read_attachment") &&
        isString(inputs.recordId)
      ) {
        const recordId = truncateQuery(inputs.recordId);
        return toolName === "list_attachments"
          ? {
              running: tp("Listing Salesforce attachments for {recordId}", {
                recordId,
              }),
              done: tp("List Salesforce attachments for {recordId}", {
                recordId,
              }),
            }
          : {
              running: tp("Reading Salesforce attachments for {recordId}", {
                recordId,
              }),
              done: tp("Read Salesforce attachments for {recordId}", {
                recordId,
              }),
            };
      }
      return null;

    case "zendesk":
      if (toolName === "get_ticket" && isNumber(inputs.ticketId)) {
        return {
          running: tp("Retrieving Zendesk ticket {ticketId}", {
            ticketId: inputs.ticketId,
          }),
          done: tp("Retrieve Zendesk ticket {ticketId}", {
            ticketId: inputs.ticketId,
          }),
        };
      }
      if (toolName === "search_tickets" && isString(inputs.query)) {
        const q = truncateQuery(inputs.query);
        return {
          running: tp("Searching Zendesk tickets “{q}”", { q }),
          done: tp("Search Zendesk tickets “{q}”", { q }),
        };
      }
      if (
        (toolName === "draft_reply" ||
          toolName === "post_reply" ||
          toolName === "update_ticket_tags") &&
        isNumber(inputs.ticketId)
      ) {
        const [running, done] =
          toolName === "draft_reply"
            ? [
                "Drafting reply to Zendesk ticket {ticketId}",
                "Draft reply to Zendesk ticket {ticketId}",
              ]
            : toolName === "post_reply"
              ? [
                  "Posting reply to Zendesk ticket {ticketId}",
                  "Post reply to Zendesk ticket {ticketId}",
                ]
              : [
                  "Updating tags on Zendesk ticket {ticketId}",
                  "Update tags on Zendesk ticket {ticketId}",
                ];
        const ticketId = inputs.ticketId;
        return {
          running: tp(running, { ticketId }),
          done: tp(done, { ticketId }),
        };
      }
      return null;

    case "sandbox":
      if (toolName === "bash" && isString(inputs.description)) {
        const description = truncateQuery(inputs.description);
        return { running: description, done: description };
      }
      return null;

    case "query_tables_v2":
      if (
        toolName === "execute_database_query" &&
        isString(inputs.description)
      ) {
        const description = truncateQuery(inputs.description);
        return { running: description, done: description };
      }
      return null;

    case "data_warehouses":
      if (toolName === "query" && isString(inputs.description)) {
        const description = truncateQuery(inputs.description);
        return { running: description, done: description };
      }
      return null;

    case "run_agent":
      if (isString(inputs.description)) {
        const d = truncateQuery(inputs.description);
        return { running: d, done: d };
      }
      return null;

    case "interactive_content":
      if (
        toolName === "create_interactive_content_file" &&
        isString(inputs.file_name)
      ) {
        const name = truncateQuery(inputs.file_name);
        return {
          running: tp("Creating Frame “{name}”", { name }),
          done: tp("Create Frame “{name}”", { name }),
        };
      }
      if (
        toolName === "edit_interactive_content_file" &&
        isString(inputs.description)
      ) {
        const d = truncateQuery(inputs.description);
        return {
          running: tp("Editing Frame: {d}", { d }),
          done: tp("Edit Frame: {d}", { d }),
        };
      }
      if (
        toolName === "rename_interactive_content_file" &&
        isString(inputs.new_file_name)
      ) {
        const name = truncateQuery(inputs.new_file_name);
        return {
          running: tp("Renaming Frame to “{name}”", { name }),
          done: tp("Rename Frame to “{name}”", { name }),
        };
      }
      return null;

    case "skill_management":
      if (toolName === "enable_skill" && isString(inputs.skillName)) {
        // Fork : libellés français, nom traduit pour les compétences de la plateforme.
        const name = truncateQuery(nomCompetenceDepuisNom(inputs.skillName));
        return {
          running: tp("Enabling skill “{name}”", { name }),
          done: tp("Enable skill “{name}”", { name }),
        };
      }
      return null;

    case "ask_user_question":
      if (toolName === "ask_user_question" && isString(inputs.question)) {
        const q = truncateQuery(inputs.question);
        return {
          running: tp("Asking: “{q}”", { q }),
          done: tp("Ask: “{q}”", { q }),
        };
      }
      return null;

    case "hubspot":
      if (toolName === "search_crm_objects" && isString(inputs.objectType)) {
        const objectType = inputs.objectType;
        const filters = inputs.filters;
        const isByIdLookup =
          Array.isArray(filters) &&
          filters.some((f) => f?.propertyName === "hs_object_id");
        if (isByIdLookup) {
          return {
            running: tp("Retrieving HubSpot {objectType}", { objectType }),
            done: tp("Retrieve HubSpot {objectType}", { objectType }),
          };
        }
        if (isString(inputs.query) && inputs.query.length > 0) {
          const q = truncateQuery(inputs.query);
          return {
            running: tp("Searching HubSpot {objectType} “{q}”", {
              objectType,
              q,
            }),
            done: tp("Search HubSpot {objectType} “{q}”", { objectType, q }),
          };
        }
        return {
          running: tp("Searching HubSpot {objectType}", { objectType }),
          done: tp("Search HubSpot {objectType}", { objectType }),
        };
      }
      return null;

    // All other servers: no dynamic labels.
    case "agent_sidekick_agent_state":
    case "agent_sidekick_context":
    case "agent_memory":
    case "agent_router":
    case "ashby":
    case "exa_people_and_company":
    case "fathom":
    case "freshservice":
    case "gong":
    case "include_data":
    case "luma":
    case "missing_action_catcher":
    case "monday":
    case "productboard":
    case "common_utilities":
    case "run_dust_app":
    case "salesloft":
    case "servicenow":
    case "skill_authoring":
    case "slab":
    case "snowflake":
    case "sound_studio":
    case "speech_generator":
    case "statuspage":
    case "toolsets":
    case "ukg_ready":
    case "user_mentions":
    case "val_town":
    case "vanta":
    case "front":
    case "triggers_management":
    case "poke":
    default:
      return null;
  }
}

function getStaticToolDisplayLabelsForServerName(
  serverName: string,
  toolName: string
): ToolDisplayLabels | null {
  const normalizedServerName = slugify(serverName);

  return (
    // Fork : libellés français des outils Gmail et Google Agenda (front/lib/compta/libelles_outils.ts).
    libellesAffichageOutilGoogle(serverName, toolName) ??
    traduireLibellesStatiques(
      INTERNAL_TOOL_DISPLAY_LABELS_BY_SERVER[normalizedServerName]?.[toolName]
    ) ??
    traduireLibellesStatiques(
      DEFAULT_REMOTE_TOOL_DISPLAY_LABELS_BY_SERVER[normalizedServerName]?.[
        toolName
      ]
    ) ??
    // Fork : libellés français des outils métier (front/lib/compta/libelles_outils.ts).
    libellesAffichageOutilMetier(serverName, toolName) ??
    null
  );
}

function getServerNameCandidates(serverName: string): string[] {
  const lastNestedSeparatorIndex = serverName.lastIndexOf(TOOL_NAME_SEPARATOR);

  if (lastNestedSeparatorIndex === -1) {
    return [serverName];
  }

  return [
    serverName,
    serverName.slice(lastNestedSeparatorIndex + TOOL_NAME_SEPARATOR.length),
  ];
}

export function getToolNameFromFunctionCallName(functionCallName: string) {
  return functionCallName.split(TOOL_NAME_SEPARATOR).at(-1) ?? functionCallName;
}

export function getToolDisplayLabels({
  internalMCPServerName,
  mcpServerName,
  toolName,
  inputs,
}: {
  internalMCPServerName?: InternalMCPServerNameType | null;
  mcpServerName?: string | null;
  toolName: string;
  inputs: Record<string, unknown>;
}): ToolDisplayLabels | null {
  if (internalMCPServerName) {
    // Fork : Gmail et Google Agenda en français, précisés par les paramètres.
    const libellesGoogle = libellesAffichageOutilGoogle(
      internalMCPServerName,
      toolName,
      inputs
    );
    if (libellesGoogle) {
      return libellesGoogle;
    }

    const dynamicLabels = getDynamicToolDisplayLabels({
      internalMCPServerName,
      toolName,
      inputs,
    });
    if (dynamicLabels) {
      return dynamicLabels;
    }

    return getStaticToolDisplayLabelsForServerName(
      internalMCPServerName,
      toolName
    );
  }

  if (mcpServerName) {
    return getStaticToolDisplayLabelsForServerName(mcpServerName, toolName);
  }

  return null;
}

export function getStaticToolDisplayLabelsFromFunctionCallName(
  functionCallName: string
): ToolDisplayLabels | null {
  const parts = getToolCallNameParts(functionCallName);

  if (!parts) {
    return null;
  }

  for (const serverName of getServerNameCandidates(parts.serverName)) {
    const displayLabels = getStaticToolDisplayLabelsForServerName(
      serverName,
      parts.toolName
    );

    if (displayLabels) {
      return displayLabels;
    }
  }

  return null;
}

export function getToolAggregateDisplayLabel({
  functionCallName,
  toolName,
}: {
  functionCallName: string;
  toolName: string;
}): string {
  return (
    getStaticToolDisplayLabelsFromFunctionCallName(functionCallName)?.done ??
    asDisplayName(toolName)
  );
}

export function getToolCallDisplayLabel(
  functionCallName: string,
  context: "running" | "done" = "done"
): string {
  return (
    getStaticToolDisplayLabelsFromFunctionCallName(functionCallName)?.[
      context
    ] ?? asDisplayName(functionCallName)
  );
}
